// Typed wrappers for the Spotify integration (`/api/v1/spotify/*`).
// Grouped by resource: settings, auth, playlists. Mirrors the contract
// shipped by the parallel backend agent.

import { api } from "@/lib/api";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface SpotifySettings {
  configured: boolean;
  /** Server never returns the secret. Client id is echoed back so the UI can
   *  show the user *which* app the credentials belong to. */
  client_id: string | null;
}

export interface SpotifyAuthStatus {
  connected: boolean;
  username?: string;
  expires_at?: string;
}

export interface SpotifyPlaylist {
  id: string;
  name: string;
  owner_name: string;
  description: string | null;
  track_count: number;
  image_url: string | null;
  monitored: boolean;
  last_synced_at: string | null;
  /** Albums successfully matched + queued from this playlist so far. */
  matched_albums: number;
  /** Albums that couldn't be matched. */
  unmatched_albums: number;
}

export interface QueueSummary {
  matched: number;
  unmatched: number;
  /** Albums skipped because we'd already processed them. */
  already_queued: number;
}

export interface AlbumMatch {
  spotify_album_id: string;
  spotify_album_title: string;
  spotify_album_artist: string;
  /** FK into our local albums table. */
  matched_album_id: number;
  match_method: "upc" | "fuzzy";
  queued_at: string;
}

export interface AlbumMiss {
  spotify_album_id: string;
  spotify_album_title: string;
  spotify_album_artist: string;
  upc: string | null;
  /** Human-readable reason. */
  error: string;
  queued_at: string;
}

export interface PlaylistAlbums {
  matched: AlbumMatch[];
  unmatched: AlbumMiss[];
}

export interface AuthStartResponse {
  /** Browser-side redirect target — `window.location.assign(url)`. */
  url: string;
}

export interface SyncResponse {
  synced: number;
}

interface CredentialsRequest {
  client_id: string;
  client_secret: string;
}

// -----------------------------------------------------------------------------
// API
// -----------------------------------------------------------------------------

export const spotifyApi = {
  settings: {
    get: () => api<SpotifySettings>("/api/v1/spotify/settings"),
    set: (body: CredentialsRequest) =>
      api<{ configured: true }>("/api/v1/spotify/settings", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    clear: () =>
      api<void>("/api/v1/spotify/settings", { method: "DELETE" }),
  },

  auth: {
    start: () =>
      api<AuthStartResponse>("/api/v1/spotify/auth/start", { method: "POST" }),
    status: () => api<SpotifyAuthStatus>("/api/v1/spotify/auth/status"),
    disconnect: () =>
      api<void>("/api/v1/spotify/auth/disconnect", { method: "POST" }),
  },

  playlists: {
    list: () => api<SpotifyPlaylist[]>("/api/v1/spotify/playlists"),
    /** Pull the latest playlists from Spotify. Returns `synced` count. */
    syncAll: () =>
      api<SyncResponse>("/api/v1/spotify/playlists/sync", { method: "POST" }),
    /** Toggle `monitored` on a playlist. Backend queues albums async on enable. */
    setMonitored: (id: string, monitored: boolean) =>
      api<SpotifyPlaylist>(
        `/api/v1/spotify/playlists/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify({ monitored }),
        },
      ),
    /** Force a sync pass on a single playlist. Returns the queue summary. */
    sync: (id: string) =>
      api<QueueSummary>(
        `/api/v1/spotify/playlists/${encodeURIComponent(id)}/sync`,
        { method: "POST" },
      ),
    /** Per-playlist match history. */
    albums: (id: string) =>
      api<PlaylistAlbums>(
        `/api/v1/spotify/playlists/${encodeURIComponent(id)}/albums`,
      ),
  },
};
